import React, { useEffect, useState } from 'react';
import { FaUserInjured } from 'react-icons/fa';
import { employeeAPI } from './config/apiService';

// SIP Safety Incentive KPI banner.
// Headline = accumulated safety points. A 12-month bar chart shows points
// earned per disbursement period (quiet gray nubs = no points; green bars =
// earned, taller / darker = more). Secondary stats: "points earned / possible"
// (sum earned vs. the achievable max over program months) and months earned.
// Injury months (0 points) within the program are flagged with an
// injured-person icon above their bar.
//
// Program started Nov 2025, which awarded a max of 4 (the ramp month); every
// month after maxes at 8. Months before Nov 2025 are outside the program and
// are not counted as missed (no "0", no injury icon).
//
// Data: GET /sip/employee/{id}/manager -> SIP_Get_Disbursement_History.

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MAX_PER_MONTH = 8;                   // full monthly award
const NOV_2025_KEY = 2025 * 12 + 10;       // program's first month (Nov 2025), ramped to 4
const PROGRAM_START_KEY = NOV_2025_KEY;    // tracking starts here

const monthKey = (dt) => dt.getFullYear() * 12 + dt.getMonth();
// Achievable points for a given month-key: Nov 2025 ramped to 4, otherwise 8.
const maxForMonth = (key) => (key === NOV_2025_KEY ? 4 : MAX_PER_MONTH);

// Tier styling for a month's earned points.
function tier(points) {
    if (points <= 0) return { bar: '#e5e7eb', text: '#9ca3af' }; // quiet - no points earned
    if (points <= 4) return { bar: '#4ade80', text: '#15803d' }; // earned - base tier
    return { bar: '#16a34a', text: '#166534' };                  // earned - bonus tier
}

function SafetyKpiBanner({ employeeId, accumulatedPoints }) {
    const [periods, setPeriods] = useState([]);

    useEffect(() => {
        if (!employeeId) return;
        let cancelled = false;
        employeeAPI.getPointsTimeline(employeeId)
            .then(res => {
                if (cancelled) return;
                const data = Array.isArray(res.data) ? res.data : [];
                // Proc returns newest-first; show oldest -> newest, last 12.
                const sorted = [...data].sort((a, b) => new Date(a.date) - new Date(b.date));
                setPeriods(sorted.slice(-12));
            })
            .catch(() => { if (!cancelled) setPeriods([]); });
        return () => { cancelled = true; };
    }, [employeeId]);

    if (!periods.length) return null;

    const TRACK = 40; // px - height of the tallest bar
    const maxPoints = Math.max(MAX_PER_MONTH, ...periods.map(p => p.newPoints || 0));
    const lastIdx = periods.length - 1;

    // Program months only (Nov 2025 onward) drive the secondary stats — earlier
    // months predate the program and aren't counted as missed.
    const programPeriods = periods.filter(p => {
        const d = new Date(p.date);
        return !isNaN(d) && monthKey(d) >= PROGRAM_START_KEY;
    });
    const earnedPts = programPeriods.reduce((sum, p) => sum + (p.newPoints || 0), 0);
    const possiblePts = programPeriods.reduce((sum, p) => sum + maxForMonth(monthKey(new Date(p.date))), 0);
    const earnedMonths = programPeriods.filter(p => (p.newPoints || 0) > 0).length;
    const programMonths = programPeriods.length;

    return (
        <div style={{
            background: '#ffffff',
            border: '1px solid #e6e8eb',
            borderRadius: '10px',
            padding: '10px 14px 8px',
            margin: '4px 0 8px',
            boxShadow: '0 1px 2px rgba(16,24,40,0.05)'
        }}>
            {/* Headline metrics */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '8px' }}>
                <div>
                    <div style={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#9ca3af' }}>
                        Total Safety Points
                    </div>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: '5px', marginTop: '1px' }}>
                        <span style={{ fontSize: '30px', fontWeight: 800, color: '#111827', lineHeight: 1 }}>{accumulatedPoints ?? 0}</span>
                        <span style={{ fontSize: '13px', fontWeight: 600, color: '#6b7280' }}>pts</span>
                    </div>
                </div>
                <div style={{ display: 'flex', gap: '16px', textAlign: 'right' }}>
                    <div>
                        <div style={{ fontSize: '16px', fontWeight: 800, color: '#d97706', lineHeight: 1.1 }}>{earnedPts}<span style={{ color: '#9ca3af' }}>/{possiblePts}</span></div>
                        <div style={{ fontSize: '9px', fontWeight: 600, color: '#9ca3af' }}>points earned / possible</div>
                    </div>
                    <div>
                        <div style={{ fontSize: '16px', fontWeight: 800, color: '#15803d', lineHeight: 1.1 }}>{earnedMonths}/{programMonths}</div>
                        <div style={{ fontSize: '9px', fontWeight: 600, color: '#9ca3af' }}>months earned</div>
                    </div>
                </div>
            </div>

            {/* Monthly bar chart */}
            <div style={{
                display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between',
                gap: '3px', marginTop: '10px', paddingBottom: '3px',
                borderBottom: '1px solid #f1f3f5', overflowX: 'auto'
            }}>
                {periods.map((p, i) => {
                    const pts = p.newPoints || 0;
                    const t = tier(pts);
                    const h = pts <= 0 ? 4 : Math.max(9, Math.round((pts / maxPoints) * TRACK));
                    const isCurrent = i === lastIdx;
                    const d = new Date(p.date);
                    const inProgram = !isNaN(d) && monthKey(d) >= PROGRAM_START_KEY;
                    const isInjury = pts <= 0 && inProgram;
                    return (
                        <div
                            key={i}
                            title={isNaN(d) ? '' : `${d.toLocaleDateString()} — ${pts} pt${pts === 1 ? '' : 's'}${isInjury ? ' (injury)' : ''}`}
                            style={{ flex: '1 0 auto', minWidth: '22px', maxWidth: '40px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}
                        >
                            <span style={{ fontSize: '11px', fontWeight: 700, color: t.text, lineHeight: '14px', height: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                {pts > 0 ? pts : (inProgram ? '0' : '')}
                            </span>
                            <div style={{ height: `${TRACK}px`, width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end' }}>
                                {isInjury && <FaUserInjured style={{ color: '#dc2626', fontSize: '15px', marginBottom: '2px' }} />}
                                <div style={{ width: '68%', maxWidth: '20px', height: `${h}px`, background: t.bar, borderRadius: '4px 4px 2px 2px' }} />
                            </div>
                            <span style={{
                                fontSize: '9px', marginTop: '5px', paddingBottom: '2px', lineHeight: '11px',
                                fontWeight: isCurrent ? 700 : 500,
                                color: isCurrent ? '#15803d' : '#9ca3af',
                                borderBottom: isCurrent ? '2px solid #16a34a' : '2px solid transparent'
                            }}>
                                {isNaN(d) ? '' : MONTHS[d.getMonth()]}
                            </span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

export default SafetyKpiBanner;
